import ArrowRightIcon from "@sprout/icons/arrow_right";
import "./profile_card.scss";
import { Body, SectionTitle, Link } from "@/sprout/typography";
import { Link as InertiaLink, usePage } from "@inertiajs/react";
import useTranslation from "@hooks/use_translation";
import CompletionTag from "@components/completion_tag.tsx";
import { CompletionStatus } from "@utils/completion_status.ts";
import useProfile from "@/queries/use_profile";
import ClearVerifiedBadge from "@/components/ui/clear_verified_badge";
import LoadingSpinner from "@/sprout/loading_spinner";
import { SecondaryButtonLink } from "@sprout/buttons";

const ProfileCardHeader = () => {
  const { t } = useTranslation("user");
  return (
    <InertiaLink href="/profile" className="profile_card__header">
      <SectionTitle>{t("profile")}</SectionTitle>
      <div className="profile_card__header__icon">
        <ArrowRightIcon />
      </div>
    </InertiaLink>
  );
};

const ProfileCard = () => {
  const { t } = useTranslation("user");
  const { data, isLoading, error } = useProfile();

  if (isLoading) {
    return (
      <div className="profile_card">
        <ProfileCardHeader />
        <div className="profile_card__content">
          <div className="flex justify-center items-center h-32">
            <LoadingSpinner size="medium" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="profile_card">
        <ProfileCardHeader />
        <div className="profile_card__content">
          <Body>{t("defaultError")}</Body>
        </div>
      </div>
    );
  }

  const {
    email,
    firstName,
    lastName,
    quickApplyCompletion,
    workPreferencesCompletion,
    isClearVerified,
  } = data;

  const hasAnyName = Boolean(firstName || lastName);

  let profileContent = null;

  if (hasAnyName) {
    profileContent = (
      <div>
        <div className="flex items-center gap-2">
          <Body>
            {firstName} {lastName}
          </Body>
          {isClearVerified && <ClearVerifiedBadge />}
        </div>
        <div>
          <Body className="profile_card__email">{email}</Body>
        </div>
      </div>
    );
  } else {
    profileContent = (
      <div className="flex items-center gap-2">
        <Body>{email}</Body>
        {isClearVerified && <ClearVerifiedBadge />}
      </div>
    );
  }

  const { uploadResumeModal } = usePage<{ uploadResumeModal?: boolean }>().props;

  const GetStartedButton = ({ href }: { href: string }) => (
    <SecondaryButtonLink href={href} size="medium">
      {t("getStarted")}
    </SecondaryButtonLink>
  );

  const linkClassName = uploadResumeModal ? "link--muted" : "";

  const NotStartedStatus = ({ href }: { href: string }) => {
    if (uploadResumeModal) {
      return <CompletionTag status={CompletionStatus.NotStarted} />;
    }
    return <GetStartedButton href={href} />;
  };

  return (
    <div className="profile_card">
      <ProfileCardHeader />
      <div className="profile_card__content">
        {profileContent}
        <hr />
        <div className="profile_card__footer">
          <div>
            <Link href="/quick_apply" className={linkClassName}>
              {t("quickApply")}
            </Link>
            {quickApplyCompletion === CompletionStatus.NotStarted ? (
              <NotStartedStatus href="/quick_apply" />
            ) : (
              <CompletionTag status={quickApplyCompletion} />
            )}
          </div>
          <div>
            <Link href="/work_preferences" className={linkClassName}>
              {t("workPreferences.title")}
            </Link>
            {workPreferencesCompletion === CompletionStatus.NotStarted ? (
              <NotStartedStatus href="/work_preferences" />
            ) : (
              <CompletionTag status={workPreferencesCompletion} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;
